// Focus utilities for the layered primitives (Popover, Menu, Modal): find what is focusable, keep Tab
// inside a container (the modal's focus trap, WAI-ARIA APG "Dialog (Modal)"), and give focus back to the
// element that had it when the layer opened (TEST_PLAN §1.6 K6: Escape closes and focus returns to the
// trigger). A few lines on purpose — ARCH §11.1 names no focus library.

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Tabbable descendants of `root`, in DOM order. jsdom does no layout, so hiding is judged by `hidden`. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.closest('[hidden]') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/**
 * Handles a Tab / Shift+Tab keydown so focus cycles inside `root`. Call from the container's `onKeyDown`;
 * it only acts on Tab and only prevents the default when it moved the focus itself.
 */
export function trapTabKey(
  event: KeyboardEvent | { key: string; shiftKey: boolean; preventDefault(): void },
  root: HTMLElement,
): void {
  if (event.key !== 'Tab') return;
  const items = focusableIn(root);
  if (items.length === 0) {
    event.preventDefault();
    root.focus();
    return;
  }
  const first = items[0] as HTMLElement;
  const last = items[items.length - 1] as HTMLElement;
  const active = document.activeElement;
  if (event.shiftKey) {
    if (active === first || !root.contains(active)) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last || !root.contains(active)) {
    event.preventDefault();
    first.focus();
  }
}

/** Moves focus into `root`: the preferred element, else the first tabbable, else the container itself. */
export function focusInto(root: HTMLElement, preferred?: HTMLElement | null): void {
  const target = preferred ?? focusableIn(root)[0] ?? root;
  target.focus({ preventScroll: true });
}

/**
 * `focusInto`, but it CHECKS, and tries again on the next frame if the focus did not land — **BUG-020**.
 *
 * An anchored layer is painted `visibility: hidden` for its first commit while `usePosition` measures the
 * anchor (`a11y/usePosition.ts`). A real browser will not focus anything inside a subtree it still considers
 * hidden, so `.focus()` is a silent no-op there and the trigger keeps the focus — which, for a `<body>`
 * portal, leaves the whole page between the user and the panel they just opened (QA measured 13 Tab stops).
 * jsdom paints nothing and focuses happily, which is why every unit test passed while the browser did not.
 *
 * Waiting on the position alone is not enough: the style recalculation the browser needs can land after the
 * commit that removed the attribute. So this asks, looks, and asks again.
 *
 * **THE RETRY IS BOUNDED IN TIME, NOT IN FRAMES — BUG-092, and the old bound was the defect.** It used to
 * stop after `maxFrames = 5`: about 80 ms on a machine painting at 60 fps, and *five paints* on a machine
 * that is not. Under load — QA saw it inside a 21-minute gate at 1024 px, and only there — the subtree can
 * still be unpaintable after five frames, so the retry gave up and the focus never arrived. That is exactly
 * REQ-281 AC2 / §1.6 K3 unmet for a keyboard user on a slow machine, which is the bug BUG-020 was; a bound
 * counted in frames says "we will wait five paints", when what a person needs is "we will wait until you
 * can actually be focused".
 *
 * So the ceiling is a DEADLINE, generous and measured in seconds. Three things keep it honest:
 *   · **it still retries on `requestAnimationFrame`**, which is the right cadence — the next paint is
 *     exactly when the answer can change — and which does not fire at all in a background tab, so a
 *     hidden panel cannot spin a CPU;
 *   · **it stops the moment the root leaves the document**, so an unmounted layer is not chased for
 *     three seconds;
 *   · the cleanup the caller already holds cancels it, unchanged.
 *
 * `performance.now()` rather than `Date.now()`: this is a DURATION, and D-020 keeps instants on the
 * injected clock.
 */
export function focusIntoWhenPaintable(
  root: HTMLElement,
  preferred?: HTMLElement | null,
  timeoutMs = 3000,
): () => void {
  let frame = 0;
  const started = typeof performance === 'object' ? performance.now() : 0;
  const elapsed = () => (typeof performance === 'object' ? performance.now() - started : timeoutMs);
  const landed = () =>
    document.activeElement === root || root.contains(document.activeElement ?? null);
  const attempt = () => {
    frame = 0;
    // A layer that has been unmounted is not going to become focusable; chasing it wastes frames and can
    // steal focus back from whatever the reader moved to next.
    if (!root.isConnected) return;
    focusInto(root, preferred);
    if (landed() || elapsed() >= timeoutMs) return;
    frame =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(attempt)
        : (setTimeout(attempt, 16) as unknown as number);
  };
  attempt();
  return () => {
    if (!frame) return;
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    else clearTimeout(frame);
  };
}

/** Returns focus to `target` if it is still in the document and focusable; otherwise leaves focus alone. */
export function restoreFocus(target: Element | null | undefined): void {
  if (target instanceof HTMLElement && target.isConnected) {
    target.focus({ preventScroll: true });
  }
}
